import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from "@angular/core";

import { AnalyticsClickDirective, ClickAreaDirective } from "@directives";
import { MenuTab } from "@models";
import { ActionService, CurrencyService } from "@services";
import { Calculator, Utils } from "@utils";

import { SvgComponent } from "../svg/svg.component";

@Component({
  selector: "mn-header",
  styleUrls: ["./header.component.scss"],
  templateUrl: "./header.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SvgComponent, AnalyticsClickDirective, ClickAreaDirective],
})
export class HeaderComponent implements AfterViewInit, OnDestroy {
  private readonly acS = inject(ActionService);
  private readonly eR = inject(ElementRef);
  private readonly crS = inject(CurrencyService);

  public prefix = this.crS.symbol;

  /*Balance*/
  protected readonly ba = signal<string>("0");
  /*Header Element Width*/
  protected readonly hW = signal<number>(0);

  /*Count*/
  protected readonly hC = computed<number>(() => {
    return this.acS.ba$().length + this.prefix.length + 1;
  });

  private rafId = 0;

  constructor() {
    effect(() => {
      this.clear();

      this.animate(untracked(this.ba), this.acS.ba$());
    });
  }

  private readonly reflowFn = (): void => {
    this.hW.set(this.eR.nativeElement.offsetWidth);
  };

  private animate(start: string, end: string): void {
    if (Calculator.eq(start, end)) {
      return;
    }

    const delta = Calculator.min(end, start);

    const startTime = performance.now();

    const update = (now: number): void => {
      const progress = Math.min((now - startTime) / Utils.second, 1);

      if (progress < 1) {
        const value = Calculator.sum(start, Calculator.mult(delta, this.ease(progress)));

        this.ba.set(this.round(value));

        this.rafId = requestAnimationFrame(update);
      } else {
        this.ba.set(this.round(end));

        this.rafId = 0;
      }
    };

    this.rafId = requestAnimationFrame(update);
  }

  public ngAfterViewInit(): void {
    this.reflowFn();

    window.addEventListener("resize", this.reflowFn);
  }

  public ngOnDestroy(): void {
    window.removeEventListener("resize", this.reflowFn);

    this.clear();
  }

  protected burger(): void {
    this.acS.mS$.set({ tab: MenuTab.M });
  }

  private ease(t: number): string {
    return (1 - (1 - t) ** 3).toFixed(6);
  }

  private clear(): void {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = 0;
    }
  }

  private round(value: string): string {
    return this.crS.round({ value, fallback: "0", full: true });
  }
}
